import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { NewsTicker } from '@components/ui/NewsTicker'

// News items - can be fetched from API
const newsItems = [
  'New Alpha release: WebGL 3.0 Engine now live',
  'Marketplace fees reduced to 5% for all creators',
  'Upcoming Tournament: Neon Nights starting Friday',
  'Join the 2M player milestone celebration',
  'New Community Wiki entries available now'
]

// Navigation links
const navLinks = [
  { label: 'Games', href: '/#games', isAnchor: true },
  { label: 'Blogs', href: '/blogs', isAnchor: false },
  { label: 'FAQ', href: '/#faq', isAnchor: true },
]

// Logo Component
function Logo({ className = "w-8 h-8" }) {
  return (
    <svg 
      version="1.2" 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 50 50" 
      className={className}
    >
      <path 
        d="M25 0.055c-4.227 0 -8.21 1.057 -11.696 2.91l21.79 7.019c8.573 2.752 13.446 11.909 10.741 20.497 -2.263 7.169 -9.03 11.672 -16.191 11.404 -2.295 -0.087 -3.864 -2.374 -3.155 -4.566 0.481 -1.498 1.901 -2.476 3.47 -2.421 4.101 0.134 7.973 -2.476 9.227 -6.609 1.498 -4.897 -1.325 -10.071 -6.199 -11.64L6.68 8.084C2.571 12.524 0.055 18.47 0.055 25c0 7.831 3.612 14.819 9.259 19.393L16.325 22.587c0.591 -1.838 2.563 -2.847 4.401 -2.256s2.847 2.563 2.256 4.401l-7.5 23.328c2.934 1.207 6.144 1.885 9.519 1.885 13.778 0 24.945 -11.167 24.945 -24.945S38.778 0.055 25 0.055" 
        fill="#e3ff04"
      />
    </svg>
  )
}

// Reusable NavLink component
function NavLink({ item, mobile, onClick, isActive }) {
  const baseClass = mobile
    ? 'px-3 py-2 rounded-lg text-sm font-medium hover:bg-white/5 transition-colors'
    : 'text-sm font-medium transition-colors'
  const activeClass = isActive 
    ? (mobile ? 'text-game-primary bg-white/5' : 'text-game-primary') 
    : (mobile ? 'text-slate-200' : 'text-slate-400 hover:text-white')
  const className = `${baseClass} ${activeClass}`

  if (item.isAnchor) {
    const handleAnchorClick = (e) => {
      const targetId = item.href.replace('/#', '')
      const element = document.getElementById(targetId)
      if (element) {
        e.preventDefault()
        element.scrollIntoView({ behavior: 'smooth' })
      }
      onClick?.()
    }
    return <a href={item.href} className={className} onClick={handleAnchorClick}>{item.label}</a>
  }
  return <Link to={item.href} className={className} onClick={onClick}>{item.label}</Link>
}

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState(null)
  const location = useLocation()

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  // Track anchor sections with Intersection Observer
  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSection(null)
      return
    }

    const anchors = navLinks.filter((l) => l.isAnchor).map((l) => l.href.replace('/#', ''))
    
    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      const elements = anchors.map((id) => document.getElementById(id)).filter(Boolean)
      if (elements.length === 0) return

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(entry.target.id)
            } else if (activeSection === entry.target.id) {
              // Section left viewport, clear active state
              setActiveSection(null)
            }
          })
        },
        { rootMargin: '-30% 0px -60% 0px', threshold: 0.1 }
      )

      elements.forEach((el) => observer.observe(el))
      
      // Cleanup
      return () => observer.disconnect()
    }, 100)

    return () => clearTimeout(timer)
  }, [location.pathname, activeSection])

  const closeMenu = () => setIsMobileMenuOpen(false)

  // Check if a nav link is active
  const isLinkActive = (href) => {
    if (href.startsWith('/#')) {
      const sectionId = href.replace('/#', '')
      return activeSection === sectionId
    }
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <NewsTicker items={newsItems} color="primary" speed={20} />

      <div className="flex justify-center px-4 lg:px-20 mt-2 max-w-350 m-auto">
        <nav className="relative bg-background-dark/70 backdrop-blur-md border border-border-dark flex items-center justify-between w-full p-3 rounded-xl">
          {/* Logo */}
          <div className="flex items-center gap-4 sm:gap-8 min-w-0">
            <Link to="/" className="flex items-center gap-2.5 min-w-0">
              <Logo className="w-8 h-8" />
              <span className="text-white text-xl font-black leading-tight tracking-[-0.015em] truncate">
                POLYTORIA
              </span>
            </Link>
          </div>

          {/* Right-side Nav + Mobile Toggle */}
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="hidden xl:flex items-center gap-8">
              {navLinks.map((item) => (
                <NavLink key={item.label} item={item} isActive={isLinkActive(item.href)} />
              ))}
            </div>

            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="xl:hidden p-2 rounded-lg border border-border-dark bg-white/5 text-white hover:bg-white/10 transition-colors"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="xl:hidden absolute left-0 right-0 top-full mt-2 bg-background-dark/95 backdrop-blur-md border border-border-dark rounded-xl p-3">
              <div className="flex flex-col gap-2">
                {navLinks.map((item) => (
                  <NavLink key={item.label} item={item} mobile onClick={closeMenu} isActive={isLinkActive(item.href)} />
                ))}
              </div>
            </div>
          )}
        </nav>
      </div>
    </header>
  )
}

